/**
 * AIReviewPanel — AI contract review panel on ContractDetail page.
 *
 * Displays a "Run AI Review" button, a prominent AI disclaimer, and
 * RedlineSuggestions grouped by Playbook category, sorted by risk rating
 * descending. Users can accept or dismiss individual suggestions.
 *
 * RBAC: Only Admin and Contract Specialist see the review trigger and
 * accept/dismiss actions.
 *
 * Requirements: 7.3, 7.4, 7.5
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  aiReviewService,
  groupSuggestionsByCategory,
  type EnrichedSuggestion,
  type GroupedSuggestions,
} from '@/services/aiReviewService';
import { playbookService } from '@/services/playbookService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';

// ─── Constants ─────────────────────────────────────────────────────────

const RISK_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  High: 'red',
  Medium: 'yellow',
  Low: 'green',
};

const AI_DISCLAIMER_TEXT =
  'These suggestions are AI-generated recommendations and do not constitute legal advice. All contract decisions should be reviewed by qualified legal counsel.';

// ─── Props ─────────────────────────────────────────────────────────────

interface AIReviewPanelProps {
  contractId: number;
  canWrite: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────

export function AIReviewPanel({ contractId, canWrite }: AIReviewPanelProps) {
  const queryClient = useQueryClient();
  const { hasAnyRole } = useAuth();
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // RBAC: Only Admin and Contract Specialist can trigger review and manage suggestions
  const canManageReview = canWrite && hasAnyRole([UserRole.Admin, UserRole.ContractSpecialist]);

  // Fetch existing suggestions
  const {
    data: suggestionsData,
    isLoading: suggestionsLoading,
    error: suggestionsError,
    refetch: refetchSuggestions,
  } = useQuery({
    queryKey: ['ai-review-suggestions', contractId],
    queryFn: () => aiReviewService.listSuggestions(contractId),
    enabled: contractId > 0,
  });

  // Fetch playbook rules for category/name enrichment
  const { data: playbookRules } = useQuery({
    queryKey: ['playbook-rules-all'],
    queryFn: () => playbookService.listRules(),
  });

  // Trigger AI review mutation
  const triggerMutation = useMutation({
    mutationFn: () => aiReviewService.triggerReview(contractId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-review-suggestions', contractId] });
    },
  });

  // Accept suggestion mutation
  const acceptMutation = useMutation({
    mutationFn: (suggestionId: number) => aiReviewService.acceptSuggestion(suggestionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-review-suggestions', contractId] });
    },
  });

  // Dismiss suggestion mutation
  const dismissMutation = useMutation({
    mutationFn: (suggestionId: number) => aiReviewService.dismissSuggestion(suggestionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-review-suggestions', contractId] });
    },
  });

  function toggleCategory(category: string) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }

  // Build grouped suggestions
  const suggestions = suggestionsData?.suggestions ?? [];
  const rules = playbookRules ?? [];
  const grouped: GroupedSuggestions[] = groupSuggestionsByCategory(suggestions, rules);
  const hasSuggestions = suggestions.length > 0;

  // Count by status
  const acceptedCount = suggestions.filter((s) => s.userDecision === 'accepted').length;
  const dismissedCount = suggestions.filter((s) => s.userDecision === 'dismissed').length;
  const pendingCount = suggestions.filter((s) => !s.userDecision).length;

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
            AI Contract Review
          </h2>
          {hasSuggestions && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
              {pendingCount > 0 && (
                <StatusBadge label={`${pendingCount} Pending`} variant="blue" />
              )}
              {acceptedCount > 0 && (
                <StatusBadge label={`${acceptedCount} Accepted`} variant="green" />
              )}
              {dismissedCount > 0 && (
                <StatusBadge label={`${dismissedCount} Dismissed`} variant="gray" />
              )}
            </div>
          )}
        </div>
        {canManageReview && (
          <button
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending}
            style={{
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: '1px solid #7c3aed',
              backgroundColor: triggerMutation.isPending ? '#ede9fe' : '#7c3aed',
              color: triggerMutation.isPending ? '#7c3aed' : '#fff',
              cursor: triggerMutation.isPending ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            {triggerMutation.isPending ? 'Running AI Review…' : 'Run AI Review'}
          </button>
        )}
      </div>

      {/* AI Review Processing State */}
      {triggerMutation.isPending && (
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '1rem',
            backgroundColor: '#f5f3ff',
            border: '1px solid #c4b5fd',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <LoadingSpinner message="Analyzing contract against playbook rules… This may take 30–60 seconds." />
        </div>
      )}

      {/* Trigger Error */}
      {triggerMutation.error && (
        <div style={{ marginBottom: '0.75rem' }}>
          <ErrorMessage
            message={(triggerMutation.error as Error)?.message ?? 'Failed to run AI review.'}
            onRetry={() => triggerMutation.mutate()}
          />
        </div>
      )}

      {/* Loading State */}
      {suggestionsLoading && (
        <LoadingSpinner message="Loading review suggestions…" />
      )}

      {/* Fetch Error */}
      {suggestionsError && !suggestionsLoading && (
        <ErrorMessage
          message={(suggestionsError as Error)?.message ?? 'Failed to load suggestions.'}
          onRetry={() => refetchSuggestions()}
        />
      )}

      {/* AI Disclaimer — always shown when suggestions exist */}
      {hasSuggestions && (
        <div
          role="alert"
          style={{
            marginBottom: '0.75rem',
            padding: '0.6rem 0.75rem',
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            fontSize: '0.8rem',
          }}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }} aria-hidden="true">⚠️</span>
          <span style={{ color: '#92400e', fontWeight: 500 }}>
            {AI_DISCLAIMER_TEXT}
          </span>
        </div>
      )}

      {/* No Suggestions State */}
      {!suggestionsLoading && !suggestionsError && !hasSuggestions && !triggerMutation.isPending && (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
          No AI review suggestions yet.{' '}
          {canManageReview && 'Click "Run AI Review" to analyze this contract.'}
        </p>
      )}

      {/* Grouped Suggestions */}
      {grouped.map((group) => (
        <CategoryGroup
          key={group.category}
          group={group}
          isCollapsed={collapsedCategories.has(group.category)}
          onToggle={() => toggleCategory(group.category)}
          canManageReview={canManageReview}
          onAccept={(id) => acceptMutation.mutate(id)}
          onDismiss={(id) => dismissMutation.mutate(id)}
          isPending={acceptMutation.isPending || dismissMutation.isPending}
        />
      ))}
    </div>
  );
}

// ─── Category Group Component ──────────────────────────────────────────

interface CategoryGroupProps {
  group: GroupedSuggestions;
  isCollapsed: boolean;
  onToggle: () => void;
  canManageReview: boolean;
  onAccept: (suggestionId: number) => void;
  onDismiss: (suggestionId: number) => void;
  isPending: boolean;
}

function CategoryGroup({
  group,
  isCollapsed,
  onToggle,
  canManageReview,
  onAccept,
  onDismiss,
  isPending,
}: CategoryGroupProps) {
  const highCount = group.suggestions.filter((s) => s.riskRating === 'High').length;
  const mediumCount = group.suggestions.filter((s) => s.riskRating === 'Medium').length;
  const lowCount = group.suggestions.filter((s) => s.riskRating === 'Low').length;

  return (
    <div
      style={{
        marginBottom: '0.75rem',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      {/* Category Header (collapsible) */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '0.6rem 0.75rem',
          backgroundColor: '#f8fafc',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: '#1e293b',
        }}
        aria-expanded={!isCollapsed}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
            {isCollapsed ? '▶' : '▼'}
          </span>
          {group.category}
          <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.8rem' }}>
            ({group.suggestions.length} suggestion{group.suggestions.length !== 1 ? 's' : ''})
          </span>
        </span>
        <span style={{ display: 'flex', gap: '0.35rem' }}>
          {highCount > 0 && <StatusBadge label={`${highCount} High`} variant="red" />}
          {mediumCount > 0 && <StatusBadge label={`${mediumCount} Med`} variant="yellow" />}
          {lowCount > 0 && <StatusBadge label={`${lowCount} Low`} variant="green" />}
        </span>
      </button>

      {/* Suggestion Cards */}
      {!isCollapsed && (
        <div style={{ padding: '0.5rem 0.75rem' }}>
          {group.suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.suggestionId}
              suggestion={suggestion}
              canManageReview={canManageReview}
              onAccept={() => onAccept(suggestion.suggestionId)}
              onDismiss={() => onDismiss(suggestion.suggestionId)}
              isPending={isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Suggestion Card Component ─────────────────────────────────────────

interface SuggestionCardProps {
  suggestion: EnrichedSuggestion;
  canManageReview: boolean;
  onAccept: () => void;
  onDismiss: () => void;
  isPending: boolean;
}

function SuggestionCard({
  suggestion,
  canManageReview,
  onAccept,
  onDismiss,
  isPending,
}: SuggestionCardProps) {
  const hasDecision = !!suggestion.userDecision;
  const isAccepted = suggestion.userDecision === 'accepted';
  const isDismissed = suggestion.userDecision === 'dismissed';

  return (
    <div
      style={{
        marginBottom: '0.5rem',
        padding: '0.75rem',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        backgroundColor: hasDecision ? '#fafafa' : '#fff',
      }}
    >
      {/* Suggestion Header: rule name + risk badge + decision badge */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
          {suggestion.ruleName}
        </span>
        <span style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          <StatusBadge
            label={suggestion.riskRating}
            variant={RISK_BADGE_VARIANTS[suggestion.riskRating] ?? 'gray'}
          />
          {isAccepted && <StatusBadge label="Accepted" variant="green" />}
          {isDismissed && <StatusBadge label="Dismissed" variant="gray" />}
        </span>
      </div>

      {/* Original Clause */}
      {suggestion.originalClauseText && (
        <div style={{ marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
            Original Clause:
          </span>
          <p
            style={{
              margin: '0.15rem 0 0',
              fontSize: '0.8rem',
              color: '#334155',
              backgroundColor: '#fef2f2',
              padding: '0.4rem 0.5rem',
              borderRadius: '4px',
              borderLeft: '3px solid #fca5a5',
            }}
          >
            {suggestion.originalClauseText}
          </p>
        </div>
      )}

      {/* Suggested Revision */}
      {suggestion.suggestedRevision && (
        <div style={{ marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
            Suggested Revision:
          </span>
          <p
            style={{
              margin: '0.15rem 0 0',
              fontSize: '0.8rem',
              color: '#334155',
              backgroundColor: '#f0fdf4',
              padding: '0.4rem 0.5rem',
              borderRadius: '4px',
              borderLeft: '3px solid #86efac',
            }}
          >
            {suggestion.suggestedRevision}
          </p>
        </div>
      )}

      {/* Explanation */}
      {suggestion.explanation && (
        <div style={{ marginBottom: '0.4rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
            Explanation:
          </span>
          <p
            style={{
              margin: '0.15rem 0 0',
              fontSize: '0.8rem',
              color: '#475569',
            }}
          >
            {suggestion.explanation}
          </p>
        </div>
      )}

      {/* Decision info or action buttons */}
      {hasDecision ? (
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.35rem' }}>
          {isAccepted && (
            <span>
              Accepted by <strong>{suggestion.decisionBy}</strong>
              {suggestion.decisionAt && (
                <> on {new Date(suggestion.decisionAt).toLocaleDateString()}</>
              )}
            </span>
          )}
          {isDismissed && (
            <span>
              Dismissed by <strong>{suggestion.decisionBy}</strong>
              {suggestion.decisionAt && (
                <> on {new Date(suggestion.decisionAt).toLocaleDateString()}</>
              )}
            </span>
          )}
        </div>
      ) : (
        canManageReview && (
          <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.5rem' }}>
            <button
              onClick={onAccept}
              disabled={isPending}
              style={{
                padding: '0.25rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #86efac',
                backgroundColor: '#f0fdf4',
                color: '#166534',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              Accept
            </button>
            <button
              onClick={onDismiss}
              disabled={isPending}
              style={{
                padding: '0.25rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                backgroundColor: '#f8fafc',
                color: '#475569',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              Dismiss
            </button>
          </div>
        )
      )}
    </div>
  );
}
